import { ArrowLeftRight, LogOut, Monitor, Moon, Sun, UserCog } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Avatar } from '@/components/ui/Avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { portalMeta } from '@/features/auth/portals'
import { useGuardians } from '@/features/auth/guardians'
import { useAuth } from '@/features/auth/useAuth'
import { usePreferences, type Theme } from '@/features/preferences/store'
import { LANGUAGES, type Language } from '@/i18n'

export function UserMenu({ accountHref }: { accountHref: string }) {
  const { t } = useTranslation()
  const { user, portal, logout } = useAuth()
  const guardians = useGuardians()
  const { locale, theme, setLocale, setTheme } = usePreferences()
  const navigate = useNavigate()
  if (!user) return null
  const otherPortals = user.portals.filter((p) => p !== portal)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="flex items-center gap-2.5 rounded-full py-1 pr-1 pl-1 transition-colors hover:bg-surface-sunken sm:pr-3"
        aria-label={t('Account menu')}
      >
        <Avatar name={user.name} size="sm" />
        <span className="hidden text-left sm:block">
          <span className="block max-w-36 truncate text-[0.8125rem] leading-tight font-semibold text-ink">
            {user.name}
          </span>
          <span className="block text-2xs leading-tight text-muted">{t(user.roleLabel)}</span>
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64">
        <div className="flex items-center gap-3 px-2.5 py-2.5">
          <Avatar name={user.name} size="md" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-ink">{user.name}</p>
            <p className="truncate text-xs text-muted">{user.email}</p>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<UserCog />} onSelect={() => void navigate(accountHref)}>
          {t('Account & preferences')}
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('Language')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={locale} onValueChange={(v) => setLocale(v as Language)}>
          {LANGUAGES.map((l) => (
            <DropdownMenuRadioItem
              key={l.code}
              value={l.code}
              lang={l.code}
              onSelect={(e) => e.preventDefault()}
            >
              {l.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('Theme')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as Theme)}>
          <DropdownMenuRadioItem value="light" icon={<Sun />} onSelect={(e) => e.preventDefault()}>
            {t('Light')}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark" icon={<Moon />} onSelect={(e) => e.preventDefault()}>
            {t('Dark')}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system" icon={<Monitor />} onSelect={(e) => e.preventDefault()}>
            {t('System')}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        {otherPortals.length ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t('Switch workspace')}</DropdownMenuLabel>
            {otherPortals.map((p) => (
              <DropdownMenuItem
                key={p}
                icon={<ArrowLeftRight />}
                onSelect={() => void navigate(`/login?portal=${p}`)}
              >
                {t(portalMeta(p, guardians).label)}
              </DropdownMenuItem>
            ))}
          </>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          icon={<LogOut />}
          tone="danger"
          onSelect={() => {
            void logout().then(() => navigate(`/login?portal=${portal ?? 'admin'}`, { replace: true }))
          }}
        >
          {t('Sign out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
